import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Save, User, AtSign } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Input from '../../common/Input';
import { updateEmail } from '../../../services/emailServiceSwitch';
import './EmailManagement.css';

/**
 * EditEmailModal Component
 * 
 * Modal for editing email recipient name (email address cannot be changed)
 */
export default function EditEmailModal({ isOpen, onClose, email, onSuccess }) {
  const [formData, setFormData] = useState({
    name: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Initialize form data when email changes
  useEffect(() => {
    if (email) {
      setFormData({
        name: email.name || ''
      });
    }
  }, [email]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Call API to update email
      await updateEmail(email.id, formData);

      // Show success notification
      if (onSuccess) {
        onSuccess('Email updated successfully');
      }

      // Close modal
      onClose();
    } catch (error) {
      setErrors({
        submit: error.message || 'Failed to update email'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      setErrors({});
      onClose();
    }
  };

  if (!email) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Email Recipient"
      size="md"
      closeOnBackdrop={!loading}
      footer={
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={loading}
            icon={<Save size={18} />}
          >
            Save Changes
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="email-form">
        {/* Email Field (Read-only) */}
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            <AtSign size={16} />
            Email Address
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={email.email}
            disabled
            readOnly
          />
          <p className="field-hint">
            Email address cannot be changed
          </p>
        </div>

        {/* Name Field */}
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            <User size={16} />
            Contact Name
          </label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Doe"
            error={errors.name}
            disabled={loading}
          />
          {errors.name && (
            <span className="error-text">{errors.name}</span>
          )}
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="form-error">
            {errors.submit}
          </div>
        )}
      </form>
    </Modal>
  );
}

EditEmailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  email: PropTypes.shape({
    id: PropTypes.number.isRequired,
    email: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  }),
  onSuccess: PropTypes.func
};
